/**
 * @fileoverview Client-side logic for when the EEA/UK/CH consent banner appears.
 * @description Extracted from the banner component so hydration timing is
 * unit-testable without React.
 *
 * @module lib/analytics/consent-banner-visibility
 */

import { hasPersistedConsentChoice } from "./consent-preferences"
import { REGION_CONSENT_REQUIRED_COOKIE } from "./cookie-names"
import { isConsentRequiredRegionClient } from "./consent-context"
import { getHydratedConsentRequiredSignal } from "./hydrate-region-cookie"

/**
 * Whether the first-visit consent banner should be visible on this route.
 *
 * Shows only when region is **confirmed** consent-required: `adsights_region_cr=1`,
 * or the region-signal API hydrated `consentRequired: true` in memory (blocked
 * Set-Cookie). Unknown geo / failed hydration does not show the banner — rest
 * of world opt-out applies until a clear EEA/UK/CH signal arrives.
 *
 * @param {string} [pathname] - Current route pathname (hides on privacy choices).
 * @returns {boolean} True when the banner should render.
 */
export function shouldShowConsentBanner(pathname?: string): boolean {
  if (typeof window === "undefined") return false
  const path = pathname ?? window.location.pathname
  if (path === "/privacy/choices") return false
  if (hasPersistedConsentChoice()) return false
  return isConsentRequiredRegionClient() || getHydratedConsentRequiredSignal()
}

/**
 * Whether the middleware/API region cookie is already present.
 *
 * @returns {boolean} True when `adsights_region_cr` is set.
 */
export function hasRegionConsentCookie(): boolean {
  if (typeof document === "undefined") return false
  try {
    return (
      document.cookie.includes(`${REGION_CONSENT_REQUIRED_COOKIE}=1`) ||
      document.cookie.includes(`${REGION_CONSENT_REQUIRED_COOKIE}=0`)
    )
  } catch {
    return false
  }
}
